import React, { useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import fetchWithToken from "views/auth/signIn/axiosInstance";
import { useTranslation } from "react-i18next";
import PAGE_IDS from "constants/pageIds";
import { GOLD_BORDER, GOLD_TEXT } from "constants/brandColors";
import { useLocales, useTranslationsState, validateNameTranslations } from "utils/translations";
import LocaleNameTabs from "components/localeNameFields/LocaleNameTabs";

const TAGS_PAGE_ID = PAGE_IDS.TAGS;

const fieldProps = {
  bg: "white",
  borderColor: GOLD_BORDER,
  focusBorderColor: GOLD_BORDER,
};

const emptyNameTranslation = () => ({ name: "" });

function CreateTag({ onCreated }) {
  const { t } = useTranslation();
  const { locales } = useLocales();
  const [translations, setTranslations, setTranslationField] = useTranslationsState(locales, emptyNameTranslation);
  const [slug, setSlug] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const errors = validateNameTranslations(translations, locales, t);
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      const translationsPayload = locales
        .map(({ code }) => ({ locale: code, name: (translations[code]?.name || "").trim() }))
        .filter((tr) => tr.name);

      const data = {
        page_id: TAGS_PAGE_ID,
        translations: translationsPayload,
        ...(slug.trim() && { slug: slug.trim() }),
      };
      const response = await fetchWithToken(
        `${process.env.REACT_APP_NEWS_API_URL}/tag`,
        "POST",
        data
      );
      if (!response) {
        throw new Error("Failed to create tag");
      }
      setTranslations(() => {
        const reset = {};
        locales.forEach(({ code }) => { reset[code] = emptyNameTranslation(); });
        return reset;
      });
      setSlug("");
      onCreated?.();
    } catch (err) {
      setError(err.message || t("Failed to create tag. Please try again later."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <FormControl>
      <form onSubmit={handleSubmit}>
        <LocaleNameTabs
          locales={locales}
          translations={translations}
          errors={fieldErrors}
          onChange={(code, value) => setTranslationField(code, "name", value)}
          borderColor={GOLD_BORDER}
        />

        <FormControl mt="16px">
          <FormLabel fontSize="sm" fontWeight="500" color={GOLD_TEXT}>{t("Slug")}</FormLabel>
          <Input placeholder={t("Leave blank to auto-generate")} value={slug} onChange={(e) => setSlug(e.target.value)} {...fieldProps} />
        </FormControl>

        <Button
          type="submit"
          fontSize="sm"
          bg={GOLD_BORDER}
          color="#1A1A1A"
          _hover={{ bg: GOLD_TEXT, color: "white" }}
          _active={{ bg: GOLD_TEXT, color: "white" }}
          fontWeight="500"
          w="100%"
          h="50px"
          mt="20px"
          mb="8px"
          isLoading={isSubmitting}
        >
          {t("Create Tag")}
        </Button>
        {error && (
          <Box color="red.500" mt={2} whiteSpace="pre-line">
            {error}
          </Box>
        )}
      </form>
    </FormControl>
  );
}

export default CreateTag;
